import React, { useState } from 'react';
import { 
  Users, Award, Calendar, Zap, Mail, Clock, 
  Search, Briefcase, MapPin, CheckCircle, 
  TrendingUp, BarChart2, PieChart, Shield,
  ChevronRight, Filter, Download, Plus, Star
} from 'lucide-react';

// --- TYPES & INTERFACES ---
interface StatCardProps {
    icon: React.ComponentType<{ className?: string }>; // Accepts an uninstantiated Lucide component safely
    title: string;
    value: string;
    change: string;
    subtext?: string;
    iconBg: string;
    iconColor: string;
  }

interface ProgressRowProps {
  label: string;
  value: number;
  percentage: number;
  total: number;
  colorClass?: string;
}

// --- MAIN DASHBOARD COMPONENT ---
export default function RecruiterDashboard() {
  const [dateRange, setDateRange] = useState('May 16 - Jun 15, 2024');

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans antialiased pb-12">
      {/* --- TOP NAVIGATION BAR --- */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 px-4 lg:px-8 py-3">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-blue-600 text-white p-2 rounded-lg font-bold text-lg tracking-tight">TH</div>
              <div>
                <span className="font-bold text-xl text-slate-900">TalentHub</span>
                <p className="text-xs text-slate-500 hidden sm:block">Find the right talent, faster.</p>
              </div>
            </div>
            {/* Mobile Profile / Menu Indicator */}
            <div className="flex items-center gap-2 md:hidden">
              <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=60" alt="Profile" className="w-8 h-8 rounded-full border border-blue-500" />
            </div>
          </div>

          <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-slate-600">
            <a href="#" className="text-blue-600 border-b-2 border-blue-600 pb-1 flex items-center gap-1"><BarChart2 className="w-4 h-4"/> Dashboard</a>
            <a href="#" className="hover:text-blue-600 transition flex items-center gap-1"><Search className="w-4 h-4"/> Search Candidates</a>
            <a href="#" className="hover:text-blue-600 transition flex items-center gap-1"><Briefcase className="w-4 h-4"/> Projects</a>
            <a href="#" className="hover:text-blue-600 transition flex items-center gap-1"><Mail className="w-4 h-4"/> Invitations</a>
            <a href="#" className="hover:text-blue-600 transition flex items-center gap-1"><PieChart className="w-4 h-4"/> Reports</a>
            <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-bold">New</span>
          </nav>

          <div className="flex items-center justify-between md:justify-end gap-4 border-t md:border-t-0 pt-3 md:pt-0">
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input type="text" placeholder="Quick Search..." className="w-full pl-9 pr-4 py-1.5 bg-slate-100 border-none rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="hidden md:flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-semibold text-slate-900">Alex Morgan</p>
                <p className="text-xs text-slate-500">Recruiter</p>
              </div>
              <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=60" alt="Profile" className="w-10 h-10 rounded-full object-cover border-2 border-slate-200" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 mt-6">
        {/* --- DASHBOARD HEADER --- */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Recruiter Dashboard</h1>
            <p className="text-sm text-slate-500">Real time overview of your talent pool and activity</p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-center bg-white border border-slate-200 rounded-lg p-1.5 shadow-sm">
            <Calendar className="w-4 h-4 text-slate-400 ml-2" />
            <input 
              type="text" 
              value={dateRange} 
              onChange={(e) => setDateRange(e.target.value)}
              className="text-sm font-medium text-slate-700 focus:outline-none bg-transparent w-44"
            />
            <button className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded">Custom Range</button>
          </div>
        </div>

        {/* --- TOP OVERVIEW CARDS --- */}
        {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          <StatCard icon={<Users />} title="Total Candidates" value="1,248" change="+12% vs last 30 days" iconBg="bg-violet-100" iconColor="text-violet-600" />
          <StatCard icon={<Award />} title="10+ Years Experience" value="1,002" change="80% of total pool" iconBg="bg-emerald-100" iconColor="text-emerald-600" />
          <StatCard icon={<Calendar />} title="Active This Month" value="842" change="+18% vs last 30 days" iconBg="bg-amber-100" iconColor="text-amber-600" />
          <StatCard icon={<Zap />} title="Available Immediately" value="312" change="+8% vs last 30 days" iconBg="bg-blue-100" iconColor="text-blue-600" />
          <StatCard icon={<Mail />} title="Invite Acceptance Rate" value="68%" change="+5% vs last 30 days" iconBg="bg-teal-100" iconColor="text-teal-600" />
          <StatCard icon={<Clock />} title="Average Response Time" value="12 hrs" change="-2 hrs vs last 30 days" iconBg="bg-rose-100" iconColor="text-rose-600" />
        </div> */}

        {/* --- TOP OVERVIEW CARDS --- */}
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
  <StatCard icon={Users} title="Total Candidates" value="1,248" change="+12% vs last 30 days" iconBg="bg-violet-100" iconColor="text-violet-600" />
  <StatCard icon={Award} title="10+ Years Experience" value="1,002" change="80% of total pool" iconBg="bg-emerald-100" iconColor="text-emerald-600" />
  <StatCard icon={Calendar} title="Active This Month" value="842" change="+18% vs last 30 days" iconBg="bg-amber-100" iconColor="text-amber-600" />
  <StatCard icon={Zap} title="Available Immediately" value="312" change="+8% vs last 30 days" iconBg="bg-blue-100" iconColor="text-blue-600" />
  <StatCard icon={Mail} title="Invite Acceptance Rate" value="68%" change="+5% vs last 30 days" iconBg="bg-teal-100" iconColor="text-teal-600" />
  <StatCard icon={Clock} title="Average Response Time" value="12 hrs" change="-2 hrs vs last 30 days" iconBg="bg-rose-100" iconColor="text-rose-600" />
</div>

        {/* --- TALENT INSIGHTS SECTION --- */}
        <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          Talent Insights <span className="text-xs font-normal text-slate-400 border border-slate-200 rounded-full px-1.5">i</span>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {/* Experience Distribution */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Experience Distribution</h3>
            <div className="space-y-3">
              <ProgressRow label="0 - 3 Years" value={146} percentage={12} total={1248} colorClass="bg-blue-500" />
              <ProgressRow label="4 - 7 Years" value={298} percentage={24} total={1248} colorClass="bg-cyan-500" />
              <ProgressRow label="8 - 10 Years" value={302} percentage={24} total={1248} colorClass="bg-amber-500" />
              <ProgressRow label="10+ Years" value={502} percentage={40} total={1248} colorClass="bg-indigo-600" />
            </div>
            <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View 10+ Years Experience (502) →</button>
          </div>

          {/* Skills Distribution */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Skills Distribution (Top 6)</h3>
            <div className="space-y-3">
              <ProgressRow label="Java" value={412} percentage={33} total={1248} />
              <ProgressRow label="React" value={298} percentage={24} total={1248} />
              <ProgressRow label="Spring Boot" value={246} percentage={20} total={1248} />
              <ProgressRow label="AWS" value={218} percentage={17} total={1248} />
              <ProgressRow label="DevOps" value={184} percentage={15} total={1248} />
              <ProgressRow label="QA Automation" value={162} percentage={13} total={1248} />
            </div>
            <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View all Java candidates (412) →</button>
          </div>

          {/* Location Distribution */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Location Distribution</h3>
            <div className="space-y-3">
              <ProgressRow label="Bangalore" value={412} percentage={33} total={1248} />
              <ProgressRow label="Mumbai" value={268} percentage={21} total={1248} />
              <ProgressRow label="Pune" value={198} percentage={16} total={1248} />
              <ProgressRow label="Hyderabad" value={174} percentage={14} total={1248} />
              <ProgressRow label="Delhi NCR" value={132} percentage={11} total={1248} />
              <ProgressRow label="Chennai" value={64} percentage={5} total={1248} />
            </div>
            <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View Bangalore candidates (412) →</button>
          </div>

          {/* Availability */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Availability (Notice Period)</h3>
            <div className="space-y-3">
              <ProgressRow label="Immediate" value={312} percentage={25} total={1248} colorClass="bg-emerald-500" />
              <ProgressRow label="15 Days" value={264} percentage={21} total={1248} colorClass="bg-blue-400" />
              <ProgressRow label="30 Days" value={364} percentage={29} total={1248} colorClass="bg-amber-400" />
              <ProgressRow label="60+ Days" value={308} percentage={25} total={1248} colorClass="bg-slate-400" />
            </div>
            <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View Immediate Joiners (312) →</button>
          </div>

          {/* Current Roles */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Current Roles (Top 6)</h3>
            <div className="space-y-3">
              <ProgressRow label="Team Lead" value={384} percentage={31} total={1248} />
              <ProgressRow label="Software Engineer" value={298} percentage={24} total={1248} />
              <ProgressRow label="Senior Software Engineer" value={236} percentage={19} total={1248} />
              <ProgressRow label="Engineering Manager" value={164} percentage={13} total={1248} />
              <ProgressRow label="Solution Architect" value={106} percentage={9} total={1248} />
              <ProgressRow label="Director" value={60} percentage={5} total={1248} />
            </div>
            <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View all Directors (60) →</button>
          </div>
        </div>

        {/* --- DEEPER ANALYTICS GRID --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {/* Activity Analytics */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Activity Analytics</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500">Active Last 7 Days</span>
                <span className="font-semibold text-slate-800">528</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500">Active Last 30 Days</span>
                <span className="font-semibold text-slate-800">842</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500">Profile Updates (30 Days)</span>
                <span className="font-semibold text-slate-800">416</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-100">
                <span className="text-slate-500">New Registrations (30 Days)</span>
                <span className="font-semibold text-slate-800">162</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500">Invite Acceptance (30 Days)</span>
                <span className="font-semibold text-slate-800">68%</span>
              </div>
            </div>
            <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View active candidates →</button>
          </div>

          {/* Invite Acceptance Trends */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-bold text-slate-800">Invite Acceptance Trends</h3>
              <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">68%</span>
            </div>
            {/* Simple Mock Sparkline vector representation */}
            <div className="h-24 w-full flex items-end gap-1 px-2 pt-4">
              <div className="bg-blue-100 hover:bg-blue-500 transition-all h-[40%] w-full rounded-t"></div>
              <div className="bg-blue-200 hover:bg-blue-500 transition-all h-[55%] w-full rounded-t"></div>
              <div className="bg-blue-300 hover:bg-blue-500 transition-all h-[48%] w-full rounded-t"></div>
              <div className="bg-blue-200 hover:bg-blue-500 transition-all h-[65%] w-full rounded-t"></div>
              <div className="bg-blue-400 hover:bg-blue-500 transition-all h-[58%] w-full rounded-t"></div>
              <div className="bg-blue-600 hover:bg-blue-500 transition-all h-[80%] w-full rounded-t"></div>
            </div>
            <div className="flex justify-between text-[10px] text-slate-400 mt-2">
              <span>May 16</span>
              <span>Jun 6</span>
              <span>Jun 13</span>
            </div>
            <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View detailed report →</button>
          </div>

          {/* Profile Completeness */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Profile Completeness</h3>
            <div className="space-y-3">
              <ProgressRow label="90-100%" value={502} percentage={40} total={1248} colorClass="bg-teal-500" />
              <ProgressRow label="70-89%" value={468} percentage={37} total={1248} colorClass="bg-blue-400" />
              <ProgressRow label="50-69%" value={206} percentage={16} total={1248} colorClass="bg-amber-400" />
              <ProgressRow label="Below 50%" value={72} percentage={6} total={1248} colorClass="bg-rose-400" />
            </div>
            <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View incomplete profiles →</button>
          </div>

          {/* Experience Level (10+ Years) */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Experience Level (10+ Years)</h3>
            <div className="space-y-3">
              <ProgressRow label="Leads / Team Lead" value={502} percentage={50} total={1002} />
              <ProgressRow label="Managers" value={278} percentage={28} total={1002} />
              <ProgressRow label="Architects" value={142} percentage={14} total={1002} />
              <ProgressRow label="Directors" value={60} percentage={6} total={1002} />
              <ProgressRow label="VP / Head" value={20} percentage={2} total={1002} />
            </div>
            <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View Directors (60) →</button>
          </div>

          {/* Expected Salary Range */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Expected Salary Range (10+ Yrs)</h3>
            <div className="space-y-3">
              <ProgressRow label="10 - 15 LPA" value={186} percentage={18} total={1002} />
              <ProgressRow label="15 - 20 LPA" value={312} percentage={31} total={1002} />
              <ProgressRow label="20 - 30 LPA" value={358} percentage={36} total={1002} />
              <ProgressRow label="30 - 40 LPA" value={226} percentage={11} total={1002} />
              <ProgressRow label="40 LPA+" value={120} percentage={4} total={1002} />
            </div>
            <button className="text-xs text-blue-600 font-semibold mt-4 text-left hover:underline">View 20-30 LPA candidates (358) →</button>
          </div>
        </div>

        {/* --- BOTTOM SECTION: DEMAND VS CANDIDATES --- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Top Skills in Demand */}
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm lg:col-span-1">
            <h3 className="text-sm font-bold text-slate-900 mb-4">Top Skills in Demand</h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { name: 'Java', count: 412 }, { name: 'React', count: 298 },
                { name: 'Spring Boot', count: 246 }, { name: 'AWS', count: 218 },
                { name: 'DevOps', count: 184 }, { name: 'Python', count: 176 },
                { name: 'Angular', count: 142 }, { name: 'Azure', count: 128 },
                { name: '.NET', count: 110 }, { name: 'QA Automation', count: 162 }
              ].map((skill, index) => (
                <div key={index} className="flex justify-between items-center bg-slate-50 p-2 rounded-lg border border-slate-100 text-xs">
                  <span className="font-medium text-slate-700">{skill.name}</span>
                  <span className="bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-bold">{skill.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recently Active Candidates */}
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm lg:col-span-2">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-bold text-slate-900">Recently Active Candidates</h3>
              <button className="text-xs text-blue-600 font-semibold hover:underline">View all active candidates →</button>
            </div>
            <div className="space-y-3">
              {[
                { name: 'Rohit Sharma', role: 'Tech Lead', exp: '12+ Years', loc: 'Bangalore', time: 'Active 2h ago', match: '92%', img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&auto=format&fit=crop&q=60' },
                { name: 'Priya Patel', role: 'Engineering Manager', exp: '11+ Years', loc: 'Mumbai', time: 'Active 3h ago', match: '90%', img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=60' },
                { name: 'Vikram Singh', role: 'Solution Architect', exp: '14+ Years', loc: 'Pune', time: 'Active 5h ago', match: '88%', img: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&auto=format&fit=crop&q=60' },
                { name: 'Sneha Iyer', role: 'QA Automation Lead', exp: '10+ Years', loc: 'Hyderabad', time: 'Active 6h ago', match: '85%', img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&auto=format&fit=crop&q=60' }
              ].map((candidate, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition">
                  <div className="flex items-center gap-3">
                    <img src={candidate.img} alt={candidate.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <h4 className="text-sm font-bold text-slate-900">{candidate.name}</h4>
                      <p className="text-xs text-slate-500">{candidate.role} • {candidate.exp}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-400">
                        <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3"/>{candidate.loc}</span>
                        <span>•</span>
                        <span>{candidate.time}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="inline-block text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-lg">
                      {candidate.match} Match
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* --- FOOTER UTILITIES (QUICK ACTIONS, RECENT SEARCHES) --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Your Recent Activity */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Your Recent Activity</h3>
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="bg-slate-50 p-2 rounded-lg"><p className="text-lg font-bold text-slate-800">32</p><p className="text-[10px] text-slate-500">Invites Sent</p></div>
              <div className="bg-slate-50 p-2 rounded-lg"><p className="text-lg font-bold text-emerald-600">22</p><p className="text-[10px] text-slate-500">Accepted</p></div>
              <div className="bg-slate-50 p-2 rounded-lg"><p className="text-lg font-bold text-slate-800">10</p><p className="text-[10px] text-slate-500">Shortlisted</p></div>
              <div className="bg-slate-50 p-2 rounded-lg"><p className="text-lg font-bold text-slate-800">05</p><p className="text-[10px] text-slate-500">Replied</p></div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <button className="w-full flex items-center gap-2 text-xs font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 p-2 rounded-lg border border-slate-150 transition">
                <Search className="w-3.5 h-3.5 text-blue-500" /> Search Candidates
              </button>
              <button className="w-full flex items-center gap-2 text-xs font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 p-2 rounded-lg border border-slate-150 transition">
                <Plus className="w-3.5 h-3.5 text-emerald-500" /> Bulk Invite Candidates
              </button>
            </div>
          </div>

          {/* Recent Searches */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Recent Searches</h3>
            <div className="space-y-1.5 text-xs text-slate-600">
              <div className="flex justify-between items-center py-1 border-b border-dashed border-slate-100">
                <span>Java Lead in Bangalore</span><span className="text-slate-400 font-medium">150 cand.</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-dashed border-slate-100">
                <span>DevOps Engineer in Pune</span><span className="text-slate-400 font-medium">98 cand.</span>
              </div>
            </div>
          </div>

          {/* Subscription Summary */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Subscription Summary</h3>
              <div className="flex justify-between items-center text-xs mb-1">
                <span className="font-bold text-slate-800">Professional Plan</span>
                <span className="text-slate-400">Renews Jul 15</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2">
                <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: '85%' }}></div>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-2"><strong>8,550</strong> / 10,000 credits used</p>
          </div>
        </div>
      </main>
    </div>
  );
}

// --- HELPER SUB-COMPONENTS ---
function StatCard({ icon: Icon, title: value, change, iconBg, iconColor, title: cardTitle }: StatCardProps) {
    return (
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between hover:shadow-md transition">
        <div className="flex items-center justify-between mb-2">
          <div className={`p-2 rounded-lg ${iconBg} ${iconColor}`}>
            {/* Render the icon cleanly without cloneElement */}
            <Icon className="w-4 h-4" /> 
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-tight">{cardTitle}</p>
          <h3 className="text-xl font-black text-slate-900 my-0.5">{value}</h3>
          <p className="text-[10px] text-slate-500 font-medium">{change}</p>
        </div>
      </div>
    );
  }

function ProgressRow({ label, value, percentage, total, colorClass = "bg-blue-600" }: ProgressRowProps) {
  return (
    <div className="text-xs">
      <div className="flex justify-between text-slate-600 font-medium mb-1">
        <span className="truncate max-w-[120px]">{label}</span>
        <span>{value} <span className="text-slate-400 font-normal">({percentage}%)</span></span>
      </div>
      <div className="w-full bg-slate-100 rounded-full h-1.5">
        <div className={`${colorClass} h-1.5 rounded-full`} style={{ width: `${percentage}%` }}></div>
      </div>
    </div>
  );
}